/**
 * ConformidadePage — Centro de conformidade (DPO Tenant).
 *
 * Brief 5 B3 (`design-references/brief-5/r1/conformidade.html`). Lente FORENSE
 * (investigar/auditar) do mesmo motor `ConflictAlert` (ADR-117) que o supervisor
 * opera no Centro de Alertas — recortes distintos, fonte de dados compartilhada,
 * componentes reusados (sem fork).
 *
 * Gate principal: `conflict-of-interest-detection` (papel DPO — `role-mapping.ts`
 * DPO_TENANT_EXTRA; Item 55). O DPO também tem `audit-events-read-sensitive`, então
 * o detalhe NÃO é redigido para ele (defense-in-depth preservada p/ quem não a tem).
 *
 * 4 abas (fidelidade ao mockup): Conflitos de interesse (forense, F1 entregue) +
 * Bolhas de confidencialidade / Capacidade legal / Acessos anômalos (honest-defer;
 * Capacidade legal linka p/ a Curadoria `/dpo/guardianship`, que já existe).
 *
 * Read-only: o DPO não tem as caps de ação do conflito (mitigate/accept/resolve —
 * do supervisor), e a aba de conflitos do mockup é de investigação.
 */
import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { CapabilityGate } from '../components/CapabilityGate';
import { ConformidadeDeferTab } from '../features/dpo/conformidade/ConformidadeDeferTab';
import { ConformidadeForensicView } from '../features/dpo/conformidade/ConformidadeForensicView';
import { PageHead } from '../features/supervisor/components';

import type React from 'react';

type Tab = 'conflitos' | 'bolhas' | 'capacidade' | 'acessos';

const TABS: { id: Tab; label: string }[] = [
  { id: 'conflitos', label: 'Conflitos de interesse' },
  { id: 'bolhas', label: 'Bolhas de confidencialidade' },
  { id: 'capacidade', label: 'Capacidade legal' },
  { id: 'acessos', label: 'Acessos anômalos' },
];

export function ConformidadePage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('conflitos');

  return (
    <CapabilityGate
      capability="conflict-of-interest-detection"
      fallback={
        <div style={{ padding: 'var(--space-6)' }}>
          {/* Sem tierMin: o gate é por PAPEL (DPO) — a cap é Professional+ E exige o
              papel; a copy neutra "Seu perfil não tem acesso" é honesta nos dois casos. */}
          <CapabilityLockedBanner capability="conflict-of-interest-detection" variant="block" />
        </div>
      }
    >
      <PageHead
        title="Centro de conformidade"
        subtitle="Auditoria forense de conflitos de interesse, bolhas de confidencialidade e acessos."
        filters={
          <div className="tab-row" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={'tab-row__btn' + (tab === t.id ? ' tab-row__btn--active' : '')}
                onClick={() => {
                  setTab(t.id);
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        }
      />

      {tab === 'conflitos' && <ConformidadeForensicView />}
      {tab === 'bolhas' && (
        <ConformidadeDeferTab
          title="Bolhas de confidencialidade"
          message="A auditoria de bolhas de confidencialidade (ADR-040) chega em breve. Conflitos do tipo vazamento de bolha já aparecem na aba Conflitos de interesse."
        />
      )}
      {tab === 'capacidade' && (
        <ConformidadeDeferTab
          title="Capacidade legal"
          message="A gestão de curadoria e capacidade legal (ADR-114) já está disponível na tela de Curadoria."
          linkTo="/dpo/guardianship"
          linkLabel="Abrir Curadoria / capacidade legal"
        />
      )}
      {tab === 'acessos' && (
        <ConformidadeDeferTab
          title="Acessos anômalos"
          message="A detecção de acessos anômalos por desvio de baseline (ConflictAlert tipo G) chega em breve."
        />
      )}
    </CapabilityGate>
  );
}
